import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sprout, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { SafeImage } from './SafeImage';

/**
 * Mobile hero slider — rotates through hero_slides on mobile.
 * Each slide shows: badge + heading + subheading + CTAs + image, one at a time.
 * Auto-advances every 5s; user can also tap dots or arrows.
 */
export default function HeroMobileSlider({ slides = [] }) {
    const [idx, setIdx] = useState(0);
    const active = slides.filter(s => s.active !== false);
    const total = active.length;

    useEffect(() => {
        if (total <= 1) return;
        const t = setInterval(() => setIdx(i => (i + 1) % total), 5000);
        return () => clearInterval(t);
    }, [total]);

    if (total === 0) return null;
    const s = active[idx];

    return (
        <div className="relative overflow-hidden" data-testid="hero-mobile-slider">
            <div className="px-4 py-8 text-white">
                <div key={`text-${idx}`} className="dg-hero-fade">
                    {s.badge_text && (
                        <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-3 py-1 rounded-full text-xs font-medium mb-3">
                            <Sprout className="h-3.5 w-3.5" /> {s.badge_text}
                        </div>
                    )}
                    <h1 className="text-3xl font-extrabold tracking-tight leading-tight">
                        {s.heading_line1}
                        {s.heading_line2 && <><br /><span style={{ color: s.highlight_color || 'hsl(142 76% 70%)' }}>{s.heading_line2}</span></>}
                    </h1>
                    {s.subheading && <p className="mt-3 text-sm text-white/85">{s.subheading}</p>}
                    <div className="mt-5 flex flex-wrap gap-2">
                        {s.cta_primary_label && s.cta_primary_link && (
                            <Link to={s.cta_primary_link}>
                                <Button data-testid={`hero-mob-cta-${idx}`} style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                                    {s.cta_primary_label} <ArrowRight className="h-4 w-4 ml-1" />
                                </Button>
                            </Link>
                        )}
                        {s.cta_secondary_label && s.cta_secondary_link && (
                            <Link to={s.cta_secondary_link}>
                                <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white">
                                    {s.cta_secondary_label}
                                </Button>
                            </Link>
                        )}
                    </div>
                </div>
                {s.image_url && (
                    <div className="mt-6 rounded-xl overflow-hidden shadow-lg dg-hero-fade" key={`img-${idx}`}>
                        <SafeImage src={s.image_url} alt={s.heading_line1 || ''} className="w-full h-56 object-cover" />
                    </div>
                )}
            </div>

            {/* Slider controls */}
            {total > 1 && (
                <>
                    <button
                        aria-label="Previous slide"
                        onClick={() => setIdx(i => (i - 1 + total) % total)}
                        className="absolute top-16 left-2 h-8 w-8 rounded-full bg-white/20 backdrop-blur flex items-center justify-center"
                        data-testid="hero-mob-prev"
                    >
                        <ChevronLeft className="h-4 w-4 text-white" />
                    </button>
                    <button
                        aria-label="Next slide"
                        onClick={() => setIdx(i => (i + 1) % total)}
                        className="absolute top-16 right-2 h-8 w-8 rounded-full bg-white/20 backdrop-blur flex items-center justify-center"
                        data-testid="hero-mob-next"
                    >
                        <ChevronRight className="h-4 w-4 text-white" />
                    </button>
                    <div className="flex items-center justify-center gap-1.5 pb-4" data-testid="hero-mob-dots">
                        {active.map((_, i) => (
                            <button
                                key={i} onClick={() => setIdx(i)} aria-label={`Go to slide ${i + 1}`}
                                className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-8 bg-white' : 'w-2 bg-white/40'}`}
                                data-testid={`hero-mob-dot-${i}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
